import type { NotificationDto, NotificationsDto } from '@bqcrm/shared';
import { ActionIcon, Button, Group, Indicator, Popover, ScrollArea, Stack, Text, Tooltip, UnstyledButton } from '@mantine/core';
import { IconBell, IconCheck, IconSettings, IconX } from '@tabler/icons-react';
import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { api } from '../api';
import { refreshCounters } from '../liveCounters';
import { notifyError, notifyOk } from '../notify';
import { NotificationSettingsModal } from './NotificationSettings';
import { QueryState } from './QueryState';

const ago = (iso: string) => {
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (m < 1) return 'przed chwilą';
  if (m < 60) return `${m} min temu`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} godz. temu`;
  const d = Math.round(h / 24);
  return d === 1 ? 'wczoraj' : `${d} dni temu`;
};
const DOT = {
  good: 'var(--mantine-color-green-6)',
  bad: 'var(--mantine-color-red-6)',
  info: 'var(--mantine-color-blue-6)',
  warn: 'var(--mantine-color-orange-6)',
} as const;
/** Zapasowe odświeżanie, gdyby strumień na żywo był niedostępny. */
const FALLBACK_MS = 45_000;

/** Oznacza powiadomienia jako przeczytane w pamięci podręcznej (od razu znikają z listy nieprzeczytanych). */
function markLocally(qc: QueryClient, ids: string[] | 'all') {
  const hit = (n: NotificationDto) => n.unread && (ids === 'all' || ids.includes(n.id));
  for (const [key, old] of qc.getQueriesData<NotificationsDto>({ queryKey: ['notifications'] })) {
    if (!old) continue;
    const removed = old.items.filter(hit).length;
    const withRead = key[1] === 'wszystkie';
    const items = withRead ? old.items.map((n) => (hit(n) ? { ...n, unread: false } : n)) : old.items.filter((n) => !hit(n));
    qc.setQueryData<NotificationsDto>(key, { ...old, items, unread: ids === 'all' ? 0 : Math.max(0, old.unread - removed) });
  }
}

/**
 * Powiadomienia na żywo: Server-Sent Events z API (`/api/notifications/stream`) — przy każdym zdarzeniu
 * odświeżamy listę. Gdy karta jest ukryta, strumień zamykamy (nie zajmuje połączenia), a po powrocie
 * otwieramy go ponownie i od razu odświeżamy. Po zerwaniu — ponowne połączenie z rosnącym odstępem.
 */
function useLiveNotifications() {
  const qc = useQueryClient();
  useEffect(() => {
    if (typeof EventSource === 'undefined') return;
    let es: EventSource | null = null;
    let retry: ReturnType<typeof setTimeout> | null = null;
    let delay = 3000;
    let stopped = false;
    // sygnał z serwera: dzwonek i liczniki zadań / serwisu (zmiany zrobione przez inne osoby i aplikację terenową)
    const refresh = () => refreshCounters(qc);
    const close = () => {
      if (retry) clearTimeout(retry);
      retry = null;
      es?.close();
      es = null;
    };
    const open = () => {
      close();
      if (stopped || document.visibilityState === 'hidden') return;
      const src = new EventSource('/api/notifications/stream', { withCredentials: true });
      es = src;
      src.addEventListener('ready', () => {
        delay = 3000;
        refresh();
      });
      src.addEventListener('changed', refresh);
      src.onerror = () => {
        // CONNECTING — przeglądarka sama ponawia; CLOSED (np. 401, błąd pośrednika) — ponawiamy my
        if (src.readyState !== EventSource.CLOSED || es !== src) return;
        es = null;
        retry = setTimeout(open, delay);
        delay = Math.min(delay * 2, 60_000);
      };
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') close();
      else {
        refresh();
        open();
      }
    };
    open();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stopped = true;
      document.removeEventListener('visibilitychange', onVisibility);
      close();
    };
  }, [qc]);
}

/** Dzwonek: zdarzenia klientów i z terenu w moich sprawach (akceptacje, podpisy, wpłaty, protokoły). */
export function NotificationBell() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [withRead, setWithRead] = useState(false);
  const [settings, setSettings] = useState(false);
  useLiveNotifications();
  const q = useQuery({
    queryKey: ['notifications', withRead ? 'wszystkie' : 'nieprzeczytane'],
    queryFn: () => api<NotificationsDto>(`/notifications${withRead ? '?withRead=1' : ''}`),
    refetchInterval: FALLBACK_MS,
    refetchOnWindowFocus: true,
  });
  const done = () => void qc.invalidateQueries({ queryKey: ['notifications'] });
  const readOne = useMutation({
    mutationFn: (id: string) => api(`/notifications/${encodeURIComponent(id)}/read`, { method: 'POST' }),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: ['notifications'] });
      markLocally(qc, [id]);
    },
    onSettled: done,
  });
  const readAll = useMutation({
    mutationFn: () => api('/notifications/read-all', { method: 'POST' }),
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: ['notifications'] });
      markLocally(qc, 'all');
    },
    onSettled: done,
  });
  // „Potwierdź obecność” — odpowiedź prosto z dzwonka; powiadomienie znika (serwer liczy je z braku odpowiedzi)
  const answer = useMutation({
    mutationFn: (v: { n: NotificationDto; present: boolean }) => api(`/calendar/entries/${v.n.entryId}/attendance/me`, { method: 'PUT', body: { present: v.present } }),
    onSuccess: (_d, v) => {
      if (v.n.unread) readOne.mutate(v.n.id);
      notifyOk(v.present ? `Potwierdzono obecność: ${v.n.title.replace(/^Potwierdź obecność: /, '')}` : 'Zgłoszono nieobecność');
      void qc.invalidateQueries({ queryKey: ['attendance', v.n.entryId] });
    },
    onError: notifyError,
    onSettled: done,
  });
  const unread = q.data?.unread ?? 0;
  const items = q.data?.items ?? [];

  return (
    <Popover opened={open} onChange={setOpen} position="bottom-end" width="min(380px, calc(100vw - 24px))" shadow="md" withinPortal>
      <Popover.Target>
        <Indicator label={unread > 9 ? '9+' : unread} size={18} color="red" disabled={!unread} offset={4}>
          <ActionIcon variant="default" size="lg" radius="md" onClick={() => setOpen((o) => !o)} aria-label={`Powiadomienia${unread ? ` (${unread} nowych)` : ''}`}>
            <IconBell size={18} />
          </ActionIcon>
        </Indicator>
      </Popover.Target>
      <Popover.Dropdown p={0}>
        <Group justify="space-between" px="md" py="sm" style={{ borderBottom: '1px solid var(--app-border)' }}>
          <Text fw={700} size="sm">
            Powiadomienia{unread ? ` (${unread})` : ''}
          </Text>
          {unread > 0 && (
            <Button size="compact-xs" variant="subtle" onClick={() => readAll.mutate()} loading={readAll.isPending}>
              Oznacz wszystkie jako przeczytane
            </Button>
          )}
        </Group>
        <ScrollArea.Autosize mah={420}>
          {q.error && !items.length ? (
            <Stack gap={6} p="md" align="flex-start">
              <Text size="sm" c="red">
                Nie udało się pobrać powiadomień.
              </Text>
              <Button size="compact-xs" variant="light" onClick={() => void q.refetch()} loading={q.isFetching}>
                Ponów
              </Button>
            </Stack>
          ) : q.isLoading ? (
            <QueryState isLoading error={null} lines={3}>
              {null}
            </QueryState>
          ) : items.length ? (
            <Stack gap={0}>
              {items.map((n) => (
                <Group
                  key={n.id}
                  gap={0}
                  wrap="nowrap"
                  align="stretch"
                  style={{
                    borderBottom: '1px solid var(--app-border)',
                    background: n.unread ? 'var(--app-highlight)' : undefined,
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <UnstyledButton
                      className="row-link"
                      pl="md"
                      pr={n.unread ? 4 : 'md'}
                      pt={10}
                      pb={n.attendance ? 4 : 10}
                      style={{ display: 'block', width: '100%' }}
                      onClick={() => {
                        // bez linku (np. „Potwierdź obecność” bez dostępu do Kalendarza) — zostajemy w dzwonku przy przyciskach
                        // (przeczytane zniknęłoby z listy razem z nimi)
                        if (!n.link) return;
                        if (n.unread) readOne.mutate(n.id);
                        setOpen(false);
                        navigate(n.link);
                      }}
                    >
                      <Group gap="sm" wrap="nowrap" align="flex-start">
                        <span
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: 99,
                            background: DOT[n.tone],
                            marginTop: 6,
                            flexShrink: 0,
                            opacity: n.unread ? 1 : 0.4,
                          }}
                        />
                        <div style={{ minWidth: 0 }}>
                          <Text size="sm" fw={n.unread ? 700 : 500} c={n.unread ? undefined : 'dimmed'} lineClamp={2}>
                            {n.title}
                          </Text>
                          <Text size="xs" c="dimmed" truncate>
                            {n.subtitle} · {ago(n.at)}
                          </Text>
                        </div>
                      </Group>
                    </UnstyledButton>
                    {n.attendance && n.entryId && (
                      // odpowiedź bez otwierania kalendarza (poza przyciskiem wiersza — bez zagnieżdżonych przycisków)
                      <Group gap={6} pl={36} pb={10}>
                        <Button
                          size="compact-xs"
                          color="green"
                          variant="light"
                          leftSection={<IconCheck size={12} />}
                          loading={answer.isPending && answer.variables?.n.id === n.id && answer.variables.present}
                          disabled={answer.isPending}
                          onClick={() => answer.mutate({ n, present: true })}
                        >
                          Będę
                        </Button>
                        <Button
                          size="compact-xs"
                          color="red"
                          variant="light"
                          leftSection={<IconX size={12} />}
                          loading={answer.isPending && answer.variables?.n.id === n.id && !answer.variables.present}
                          disabled={answer.isPending}
                          onClick={() => answer.mutate({ n, present: false })}
                        >
                          Nie będę
                        </Button>
                      </Group>
                    )}
                  </div>
                  {n.unread && (
                    <Tooltip label="Oznacz jako przeczytane" withinPortal openDelay={400}>
                      <ActionIcon variant="subtle" color="gray" size="sm" mt={8} mr={8} onClick={() => readOne.mutate(n.id)} aria-label={`Oznacz jako przeczytane: ${n.title}`}>
                        <IconX size={14} />
                      </ActionIcon>
                    </Tooltip>
                  )}
                </Group>
              ))}
              {q.data && q.data.more > 0 && (
                <Text size="xs" c="dimmed" px="md" py={8}>
                  …i {q.data.more} starszych{withRead ? '' : ' nieprzeczytanych'}
                </Text>
              )}
            </Stack>
          ) : (
            <Text size="sm" c="dimmed" p="md">
              {withRead ? 'Brak powiadomień z ostatnich 30 dni.' : 'Brak nowych powiadomień.'} Co i gdzie dostajesz — ustawisz w „Ustawienia powiadomień” poniżej.
            </Text>
          )}
        </ScrollArea.Autosize>
        <Group justify="center" gap="xs" py={6} style={{ borderTop: '1px solid var(--app-border)' }}>
          <Button size="compact-xs" variant="subtle" color="gray" onClick={() => setWithRead((w) => !w)}>
            {withRead ? 'Pokaż tylko nieprzeczytane' : 'Pokaż przeczytane'}
          </Button>
          <Button size="compact-xs" variant="subtle" color="gray" leftSection={<IconSettings size={12} />} onClick={() => setSettings(true)}>
            Ustawienia powiadomień
          </Button>
        </Group>
      </Popover.Dropdown>
      <NotificationSettingsModal opened={settings} onClose={() => setSettings(false)} />
    </Popover>
  );
}
